import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  MessageCircle,
  Send,
  Users,
  Clock,
  CheckCircle2,
  X,
  Trash2,
  RefreshCw,
  ChevronDown,
  Building2,
  Mail,
  Inbox,
  ArrowRight,
  Reply,
  Radio,
  CheckSquare,
  Lock,
  Eye,
  EyeOff,
  LogOut,
  ShieldCheck
} from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const DEPARTMENTS = [
  { id: 'finance', name: 'Finance Department', color: 'emerald', icon: '💰' },
  { id: 'choir', name: 'Choir Department', color: 'purple', icon: '🎵' },
  { id: 'usher', name: 'Usher Department', color: 'amber', icon: '🚪' },
  { id: 'media', name: 'Media Department', color: 'blue', icon: '📺' },
  { id: 'secretary', name: 'Secretary Department', color: 'slate', icon: '📝' }
];

// Login component for Shepherd authentication
const ShepherdLogin = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/shepherd/login`, {
        username,
        password
      });

      if (response.data.success) {
        // Store token in sessionStorage
        sessionStorage.setItem('shepherd_token', response.data.token);
        sessionStorage.setItem('shepherd_name', response.data.shepherd_name);
        onLogin(response.data.token, response.data.shepherd_name);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[400px] flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full mx-4"
      >
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
            <Lock className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800">Shepherd's Corner</h2>
          <p className="text-slate-500 mt-2">Protected Access Required</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-5">
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm"
            >
              {error}
            </motion.div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Username
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-purple-400 focus:outline-none transition-colors"
              placeholder="Enter username"
              required
              data-testid="shepherd-username-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-purple-400 focus:outline-none transition-colors pr-12"
                placeholder="Enter password"
                required
                data-testid="shepherd-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <motion.button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-gradient-to-r from-purple-500 to-indigo-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
            whileHover={{ scale: loading ? 1 : 1.02 }}
            whileTap={{ scale: 0.98 }}
            data-testid="shepherd-login-button"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Authenticating...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <ShieldCheck className="w-5 h-5" />
                Access Shepherd's Corner
              </span>
            )}
          </motion.button>
        </form>

        <p className="text-center text-xs text-slate-400 mt-6">
          Access restricted to authorized personnel only
        </p>
      </motion.div>
    </div>
  );
};

const ShepherdCornerSection = ({ adminUser }) => {
  // Authentication state
  const [isShepherdAuthenticated, setIsShepherdAuthenticated] = useState(false);
  const [shepherdToken, setShepherdToken] = useState('');
  const [shepherdName, setShepherdName] = useState('');

  const [activeView, setActiveView] = useState('sent'); // 'sent' | 'inbox' | 'contact'
  const [messages, setMessages] = useState([]);
  const [inboxMessages, setInboxMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCompose, setShowCompose] = useState(false);
  const [showContactShepherd, setShowContactShepherd] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [selectedInboxMessage, setSelectedInboxMessage] = useState(null);
  const [filterDept, setFilterDept] = useState('');
  const [stats, setStats] = useState({ outgoing: { total: 0, unread: 0 }, inbox: { total: 0, unread: 0 } });
  const [replyText, setReplyText] = useState('');
  
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    target_department: '',
    isBroadcast: false
  });

  const [contactForm, setContactForm] = useState({
    subject: '',
    message: '',
    from_department: ''
  });

  const isShepherd = adminUser?.role === 'shepherd' || adminUser?.permissions?.includes('shepherd');
  const isMainAdmin = adminUser?.role === 'main_admin';
  const userPermissions = adminUser?.permissions || [];
  const canViewInbox = isShepherd || isMainAdmin || isShepherdAuthenticated;

  // Check for existing session on mount
  useEffect(() => {
    const token = sessionStorage.getItem('shepherd_token');
    const name = sessionStorage.getItem('shepherd_name');
    if (token && name) {
      setShepherdToken(token);
      setShepherdName(name);
      setIsShepherdAuthenticated(true);
    }
  }, []);

  // Handle login
  const handleShepherdLogin = (token, name) => {
    setShepherdToken(token);
    setShepherdName(name);
    setIsShepherdAuthenticated(true);
  };

  // Handle logout
  const handleShepherdLogout = async () => {
    try {
      await axios.post(`${API}/shepherd/logout?token=${shepherdToken}`);
    } catch (error) {
      console.error('Logout error:', error);
    }
    sessionStorage.removeItem('shepherd_token');
    sessionStorage.removeItem('shepherd_name');
    setShepherdToken('');
    setShepherdName('');
    setIsShepherdAuthenticated(false);
  };

  // Show login if not authenticated (unless user is main_admin or shepherd role)
  if (!isShepherdAuthenticated && !isMainAdmin && !isShepherd) {
    return <ShepherdLogin onLogin={handleShepherdLogin} />;
  }

  const loadMessages = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/shepherd/messages`, {
        params: {
          user_id: adminUser?.id || '',
          user_permissions: userPermissions.join(',')
        }
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
    setLoading(false);
  }, [adminUser?.id, userPermissions]);

  const loadInbox = useCallback(async () => {
    if (!canViewInbox) return;
    try {
      const response = await axios.get(`${API}/shepherd/inbox`, {
        params: {
          user_id: adminUser?.id || '',
          user_permissions: userPermissions.join(',')
        }
      });
      setInboxMessages(response.data);
    } catch (error) {
      console.error('Error loading inbox:', error);
    }
  }, [canViewInbox, adminUser?.id, userPermissions]);

  const loadStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/shepherd/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }, []);

  useEffect(() => { 
    loadMessages(); 
    loadInbox();
    loadStats();
  }, [loadMessages, loadInbox, loadStats]);

  const handleSendMessage = async () => {
    if (!formData.subject || !formData.message || (!formData.target_department && !formData.isBroadcast)) {
      alert('Please fill in all fields');
      return;
    }

    try {
      await axios.post(`${API}/shepherd/messages`, {
        subject: formData.subject,
        message: formData.message,
        target_department: formData.isBroadcast ? 'all' : formData.target_department
      }, {
        params: {
          sender_id: adminUser?.id || '',
          sender_name: adminUser?.full_name || 'Shepherd'
        }
      });
      
      setFormData({ subject: '', message: '', target_department: '', isBroadcast: false });
      setShowCompose(false);
      loadMessages();
      loadStats();
    } catch (error) {
      console.error('Error sending message:', error);
      alert(error.response?.data?.detail || 'Failed to send message');
    }
  };

  const handleContactShepherd = async () => {
    if (!contactForm.subject || !contactForm.message || !contactForm.from_department) {
      alert('Please fill in all fields');
      return;
    }

    try {
      await axios.post(`${API}/shepherd/inbox`, contactForm, {
        params: {
          sender_id: adminUser?.id || '',
          sender_name: adminUser?.full_name || ''
        }
      });
      
      setContactForm({ subject: '', message: '', from_department: '' });
      setShowContactShepherd(false);
      alert('Message sent to Shepherd successfully! An email notification has been sent.');
      loadStats();
    } catch (error) {
      console.error('Error sending message to shepherd:', error);
      alert(error.response?.data?.detail || 'Failed to send message');
    }
  };

  const handleMarkRead = async (messageId) => {
    try {
      await axios.patch(`${API}/shepherd/messages/${messageId}/read`, null, {
        params: { user_id: adminUser?.id || '' }
      });
      loadMessages();
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const handleMarkInboxRead = async (messageId) => {
    try {
      await axios.patch(`${API}/shepherd/inbox/${messageId}/read`);
      loadInbox();
      loadStats();
    } catch (error) {
      console.error('Error marking inbox message as read:', error);
    }
  };

  const handleReplyToInbox = async (messageId) => {
    if (!replyText.trim()) {
      alert('Please enter a reply');
      return;
    }

    try {
      await axios.post(`${API}/shepherd/inbox/${messageId}/reply`, null, {
        params: { reply: replyText, replied_by: adminUser?.id || '' }
      });
      setReplyText('');
      setSelectedInboxMessage(null);
      loadInbox();
      alert('Reply saved successfully');
    } catch (error) {
      console.error('Error replying:', error);
      alert('Failed to save reply');
    }
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    
    try {
      await axios.delete(`${API}/shepherd/messages/${messageId}`);
      setSelectedMessage(null);
      loadMessages();
      loadStats();
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  const handleDeleteInbox = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    
    try {
      await axios.delete(`${API}/shepherd/inbox/${messageId}`);
      setSelectedInboxMessage(null);
      loadInbox();
      loadStats();
    } catch (error) {
      console.error('Error deleting inbox message:', error);
    }
  };

  const getDepartmentInfo = (deptId) => {
    return DEPARTMENTS.find(d => d.id === deptId) || { name: deptId, color: 'slate', icon: '📋' };
  };

  const filteredMessages = filterDept 
    ? messages.filter(m => m.target_department === filterDept)
    : messages;

  // Get departments the user can view/access
  const accessibleDepts = isMainAdmin || isShepherd 
    ? DEPARTMENTS 
    : DEPARTMENTS.filter(d => userPermissions.includes(d.id));

  // Departments the user can send from (for contacting shepherd)
  const userDepartments = DEPARTMENTS.filter(d => userPermissions.includes(d.id));

  return (
    <div className="space-y-6" data-testid="shepherd-corner-section">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 rounded-3xl p-6 text-white shadow-xl"
      >
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-white/20 backdrop-blur-sm">
              <MessageCircle className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Shepherd's Corner</h2>
              <p className="text-purple-200">
                {canViewInbox ? '2-way communication with departments' : 'Messages from & to the Shepherd'}
              </p>
              {canViewInbox && (
                <p className="text-xs text-purple-300 mt-1">
                  📧 shepherd@cccvob.com linked
                </p>
              )}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <motion.button
              onClick={() => { loadMessages(); loadInbox(); loadStats(); }}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/20 hover:bg-white/30 transition-colors font-medium"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <RefreshCw className="w-5 h-5" />
              Refresh
            </motion.button>
            {/* Contact Shepherd button for department heads */}
            {!isShepherd && !isMainAdmin && userDepartments.length > 0 && (
              <motion.button
                onClick={() => setShowContactShepherd(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-500 text-white font-bold hover:bg-amber-600 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Mail className="w-5 h-5" />
                Contact Shepherd
              </motion.button>
            )}
            {(isShepherd || isMainAdmin) && (
              <motion.button
                onClick={() => setShowCompose(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white text-purple-600 font-bold hover:bg-purple-50 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Send className="w-5 h-5" />
                New Message
              </motion.button>
            )}
            {isShepherdAuthenticated && !isMainAdmin && !isShepherd && (
              <motion.button
                onClick={handleShepherdLogout}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-red-500/20 text-white font-medium hover:bg-red-500/30 transition-colors border border-red-400/30"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <LogOut className="w-4 h-4" />
                Logout
              </motion.button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-white/20 rounded-xl p-4 text-center backdrop-blur-sm">
            <p className="text-3xl font-bold">{stats.outgoing?.total || messages.length}</p>
            <p className="text-sm text-purple-200">Sent Messages</p>
          </div>
          {canViewInbox && (
            <div className="bg-white/20 rounded-xl p-4 text-center backdrop-blur-sm relative">
              <p className="text-3xl font-bold">{stats.inbox?.total || 0}</p>
              <p className="text-sm text-purple-200">Inbox Messages</p>
              {stats.inbox?.unread > 0 && (
                <span className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-xs font-bold">
                  {stats.inbox.unread}
                </span>
              )}
            </div>
          )}
          <div className="bg-white/20 rounded-xl p-4 text-center backdrop-blur-sm">
            <p className="text-3xl font-bold">{accessibleDepts.length}</p>
            <p className="text-sm text-purple-200">Departments</p>
          </div>
          <div className="bg-white/20 rounded-xl p-4 text-center backdrop-blur-sm">
            <p className="text-3xl font-bold">{canViewInbox ? '✓' : '—'}</p>
            <p className="text-sm text-purple-200">{canViewInbox ? 'Full Access' : 'View Only'}</p>
          </div>
        </div>
      </motion.div>

      {/* View Toggle (for Shepherd/Admin) */}
      {canViewInbox && (
        <div className="bg-white rounded-2xl p-4 shadow-lg border border-slate-100">
          <div className="flex gap-2">
            <button
              onClick={() => setActiveView('sent')}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${
                activeView === 'sent' 
                  ? 'bg-purple-500 text-white shadow-lg' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <Send className="w-4 h-4" />
              Sent Messages
              <span className="px-2 py-0.5 rounded-full text-xs bg-white/20">{messages.length}</span>
            </button>
            <button
              onClick={() => setActiveView('inbox')}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all relative ${
                activeView === 'inbox' 
                  ? 'bg-indigo-500 text-white shadow-lg' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <Inbox className="w-4 h-4" />
              Inbox
              {stats.inbox?.unread > 0 && (
                <span className="px-2 py-0.5 rounded-full text-xs bg-red-500 text-white">
                  {stats.inbox.unread} new
                </span>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Department Filters (for sent messages view) */}
      {activeView === 'sent' && (
        <div className="bg-white rounded-2xl p-4 shadow-lg border border-slate-100">
          <div className="flex items-center gap-2 mb-3">
            <Building2 className="w-5 h-5 text-slate-500" />
            <span className="font-medium text-slate-700">Filter by Department</span>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2">
            <button
              onClick={() => setFilterDept('')}
              className={`px-4 py-2 rounded-xl font-medium whitespace-nowrap transition-all ${
                !filterDept ? 'bg-purple-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              All Departments
            </button>
            {accessibleDepts.map(dept => (
              <button
                key={dept.id}
                onClick={() => setFilterDept(dept.id)}
                className={`px-4 py-2 rounded-xl font-medium whitespace-nowrap transition-all flex items-center gap-2 ${
                  filterDept === dept.id 
                    ? 'bg-purple-500 text-white' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <span>{dept.icon}</span>
                {dept.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Compose Modal (Shepherd sending to departments) */}
      <AnimatePresence>
        {showCompose && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowCompose(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-purple-50 to-indigo-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-500">
                      <Send className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-800">New Message</h3>
                  </div>
                  <button
                    onClick={() => setShowCompose(false)}
                    className="p-2 rounded-xl hover:bg-slate-100 transition-colors"
                  >
                    <X className="w-6 h-6 text-slate-600" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                {/* Broadcast Toggle */}
                <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200">
                  <input
                    type="checkbox"
                    id="broadcast"
                    checked={formData.isBroadcast}
                    onChange={e => setFormData(prev => ({ 
                      ...prev, 
                      isBroadcast: e.target.checked,
                      target_department: e.target.checked ? '' : prev.target_department
                    }))}
                    className="w-5 h-5 text-amber-500 rounded"
                  />
                  <label htmlFor="broadcast" className="flex items-center gap-2 cursor-pointer">
                    <Radio className="w-5 h-5 text-amber-600" />
                    <div>
                      <p className="font-semibold text-amber-800">Broadcast to ALL Departments</p>
                      <p className="text-xs text-amber-600">Send this message to every department at once</p>
                    </div>
                  </label>
                </div>

                {/* Department Selection (hidden if broadcast) */}
                {!formData.isBroadcast && (
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      <Users className="w-4 h-4 inline mr-2" />
                      Target Department *
                    </label>
                    <select
                      className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-purple-400 focus:outline-none transition-colors"
                      value={formData.target_department}
                      onChange={e => setFormData(prev => ({ ...prev, target_department: e.target.value }))}
                    >
                      <option value="">Select a department...</option>
                      {DEPARTMENTS.map(dept => (
                        <option key={dept.id} value={dept.id}>
                          {dept.icon} {dept.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Subject *</label>
                  <input
                    type="text"
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-purple-400 focus:outline-none transition-colors"
                    placeholder="Message subject..."
                    value={formData.subject}
                    onChange={e => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Message *</label>
                  <textarea
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-purple-400 focus:outline-none transition-colors resize-none"
                    rows={5}
                    placeholder="Write your message to the department..."
                    value={formData.message}
                    onChange={e => setFormData(prev => ({ ...prev, message: e.target.value }))}
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowCompose(false)}
                    className="flex-1 py-3 rounded-xl border-2 border-slate-300 text-slate-600 font-medium hover:bg-slate-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <motion.button
                    onClick={handleSendMessage}
                    className={`flex-1 py-3 rounded-xl text-white font-bold shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 ${
                      formData.isBroadcast 
                        ? 'bg-gradient-to-r from-amber-500 to-orange-500' 
                        : 'bg-gradient-to-r from-purple-500 to-indigo-500'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {formData.isBroadcast ? <Radio className="w-5 h-5" /> : <Send className="w-5 h-5" />}
                    {formData.isBroadcast ? 'Broadcast to All' : 'Send Message'}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Contact Shepherd Modal (Department heads sending TO shepherd) */}
      <AnimatePresence>
        {showContactShepherd && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowContactShepherd(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-amber-50 to-orange-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500">
                      <Mail className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-800">Contact the Shepherd</h3>
                      <p className="text-xs text-slate-500">Your message will be sent to shepherd@cccvob.com</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowContactShepherd(false)}
                    className="p-2 rounded-xl hover:bg-slate-100 transition-colors"
                  >
                    <X className="w-6 h-6 text-slate-600" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    <Building2 className="w-4 h-4 inline mr-2" />
                    From Department *
                  </label>
                  <select
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 focus:outline-none transition-colors"
                    value={contactForm.from_department}
                    onChange={e => setContactForm(prev => ({ ...prev, from_department: e.target.value }))}
                  >
                    <option value="">Select your department...</option>
                    {userDepartments.map(dept => (
                      <option key={dept.id} value={dept.id}>
                        {dept.icon} {dept.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Subject *</label>
                  <input
                    type="text"
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 focus:outline-none transition-colors"
                    placeholder="Message subject..."
                    value={contactForm.subject}
                    onChange={e => setContactForm(prev => ({ ...prev, subject: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Message *</label>
                  <textarea
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 focus:outline-none transition-colors resize-none"
                    rows={5}
                    placeholder="Write your message to the Shepherd..."
                    value={contactForm.message}
                    onChange={e => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                  />
                </div>

                <div className="bg-blue-50 p-3 rounded-xl border border-blue-200">
                  <p className="text-sm text-blue-700">
                    <Mail className="w-4 h-4 inline mr-1" />
                    An email notification will be sent to <strong>shepherd@cccvob.com</strong>
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowContactShepherd(false)}
                    className="flex-1 py-3 rounded-xl border-2 border-slate-300 text-slate-600 font-medium hover:bg-slate-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <motion.button
                    onClick={handleContactShepherd}
                    className="flex-1 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Send className="w-5 h-5" />
                    Send to Shepherd
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Message Detail Modal (Sent Messages) */}
      <AnimatePresence>
        {selectedMessage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedMessage(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-purple-50 to-indigo-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{getDepartmentInfo(selectedMessage.target_department).icon}</span>
                    <div>
                      <h3 className="text-lg font-bold text-slate-800">{selectedMessage.subject}</h3>
                      <p className="text-sm text-slate-500">
                        To: {getDepartmentInfo(selectedMessage.target_department).name}
                        {selectedMessage.is_broadcast && (
                          <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs">
                            Broadcast
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedMessage(null)}
                    className="p-2 rounded-xl hover:bg-slate-100 transition-colors"
                  >
                    <X className="w-6 h-6 text-slate-600" />
                  </button>
                </div>
              </div>

              <div className="p-6">
                <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
                  <Clock className="w-4 h-4" />
                  {new Date(selectedMessage.created_at).toLocaleString()}
                  <span className="mx-2">•</span>
                  <span>From: {selectedMessage.sender_name || 'Shepherd'}</span>
                </div>

                <div className="bg-slate-50 rounded-xl p-4 mb-6">
                  <p className="text-slate-700 whitespace-pre-wrap">{selectedMessage.message}</p>
                </div>

                <div className="flex gap-3">
                  {!selectedMessage.read_by?.includes(adminUser?.id) && (
                    <motion.button
                      onClick={() => handleMarkRead(selectedMessage.id)}
                      className="flex-1 py-3 rounded-xl bg-emerald-500 text-white font-medium hover:bg-emerald-600 transition-colors flex items-center justify-center gap-2"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <CheckCircle2 className="w-5 h-5" />
                      Mark as Read
                    </motion.button>
                  )}
                  {(isShepherd || isMainAdmin) && (
                    <motion.button
                      onClick={() => handleDelete(selectedMessage.id)}
                      className="py-3 px-4 rounded-xl bg-red-100 text-red-600 font-medium hover:bg-red-200 transition-colors flex items-center justify-center gap-2"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Trash2 className="w-5 h-5" />
                    </motion.button>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Inbox Message Detail Modal */}
      <AnimatePresence>
        {selectedInboxMessage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedInboxMessage(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-indigo-50 to-blue-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{getDepartmentInfo(selectedInboxMessage.from_department).icon}</span>
                    <div>
                      <h3 className="text-lg font-bold text-slate-800">{selectedInboxMessage.subject}</h3>
                      <p className="text-sm text-slate-500">
                        From: {getDepartmentInfo(selectedInboxMessage.from_department).name}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedInboxMessage(null)}
                    className="p-2 rounded-xl hover:bg-slate-100 transition-colors"
                  >
                    <X className="w-6 h-6 text-slate-600" />
                  </button>
                </div>
              </div>

              <div className="p-6">
                <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
                  <Clock className="w-4 h-4" />
                  {new Date(selectedInboxMessage.created_at).toLocaleString()}
                  <span className="mx-2">•</span>
                  <span>By: {selectedInboxMessage.sender_name || 'Unknown'}</span>
                </div>

                <div className="bg-slate-50 rounded-xl p-4 mb-6">
                  <p className="text-slate-700 whitespace-pre-wrap">{selectedInboxMessage.message}</p>
                </div>

                {/* Previous Reply */}
                {selectedInboxMessage.reply && (
                  <div className="bg-green-50 rounded-xl p-4 mb-6 border border-green-200">
                    <div className="flex items-center gap-2 text-sm text-green-700 mb-2">
                      <Reply className="w-4 h-4" />
                      <span className="font-medium">Reply from {selectedInboxMessage.replied_by || 'Shepherd'}</span>
                      {selectedInboxMessage.replied_at && (
                        <span className="text-green-600">
                          • {new Date(selectedInboxMessage.replied_at).toLocaleString()}
                        </span>
                      )}
                    </div>
                    <p className="text-green-800 whitespace-pre-wrap">{selectedInboxMessage.reply}</p>
                  </div>
                )}

                {/* Reply Form */}
                {!selectedInboxMessage.reply && (
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      <Reply className="w-4 h-4 inline mr-1" />
                      Write a Reply
                    </label>
                    <textarea
                      className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-indigo-400 focus:outline-none transition-colors resize-none"
                      rows={3}
                      placeholder="Type your reply..."
                      value={replyText}
                      onChange={e => setReplyText(e.target.value)}
                    />
                  </div>
                )}

                <div className="flex gap-3">
                  {!selectedInboxMessage.reply && (
                    <motion.button
                      onClick={() => handleReplyToInbox(selectedInboxMessage.id)}
                      className="flex-1 py-3 rounded-xl bg-indigo-500 text-white font-medium hover:bg-indigo-600 transition-colors flex items-center justify-center gap-2"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Reply className="w-5 h-5" />
                      Save Reply
                    </motion.button>
                  )}
                  <motion.button
                    onClick={() => handleDeleteInbox(selectedInboxMessage.id)}
                    className="py-3 px-4 rounded-xl bg-red-100 text-red-600 font-medium hover:bg-red-200 transition-colors flex items-center justify-center gap-2"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Trash2 className="w-5 h-5" />
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content - Sent Messages */}
      {activeView === 'sent' && (
        <>
          {loading ? (
            <div className="bg-white rounded-2xl p-12 text-center shadow-lg">
              <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-slate-500">Loading messages...</p>
            </div>
          ) : filteredMessages.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-white rounded-3xl p-12 text-center shadow-lg"
            >
              <Mail className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-slate-700 mb-2">No Messages</h3>
              <p className="text-slate-500">
                {isShepherd 
                  ? "Send your first message to a department" 
                  : "No messages have been sent to your departments yet"}
              </p>
            </motion.div>
          ) : (
            <div className="space-y-4">
              {filteredMessages.map((message, index) => {
                const deptInfo = getDepartmentInfo(message.target_department);
                const isUnread = !message.read_by?.includes(adminUser?.id);
                
                return (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => {
                      setSelectedMessage(message);
                      if (isUnread) handleMarkRead(message.id);
                    }}
                    className={`bg-white rounded-2xl p-5 shadow-lg border-2 cursor-pointer transition-all hover:shadow-xl ${
                      isUnread ? 'border-purple-300 bg-purple-50/50' : 'border-slate-100'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-xl bg-${deptInfo.color}-100 flex-shrink-0`}>
                        <span className="text-2xl">{deptInfo.icon}</span>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium bg-${deptInfo.color}-100 text-${deptInfo.color}-700`}>
                            {deptInfo.name}
                          </span>
                          {message.is_broadcast && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                              📢 Broadcast
                            </span>
                          )}
                          {isUnread && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-purple-500 text-white">
                              NEW
                            </span>
                          )}
                        </div>
                        <h3 className={`font-bold text-slate-800 mb-1 ${isUnread ? 'text-purple-800' : ''}`}>
                          {message.subject}
                        </h3>
                        <p className="text-sm text-slate-500 line-clamp-2">{message.message}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(message.created_at).toLocaleDateString()}
                          </span>
                          <span>From: {message.sender_name || 'Shepherd'}</span>
                        </div>
                      </div>

                      <ChevronDown className="w-5 h-5 text-slate-400 transform -rotate-90" />
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Content - Inbox Messages */}
      {activeView === 'inbox' && canViewInbox && (
        <>
          {inboxMessages.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-white rounded-3xl p-12 text-center shadow-lg"
            >
              <Inbox className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-slate-700 mb-2">Inbox Empty</h3>
              <p className="text-slate-500">No messages from departments yet</p>
            </motion.div>
          ) : (
            <div className="space-y-4">
              {inboxMessages.map((message, index) => {
                const deptInfo = getDepartmentInfo(message.from_department);
                
                return (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => {
                      setSelectedInboxMessage(message);
                      if (!message.is_read) handleMarkInboxRead(message.id);
                    }}
                    className={`bg-white rounded-2xl p-5 shadow-lg border-2 cursor-pointer transition-all hover:shadow-xl ${
                      !message.is_read ? 'border-indigo-300 bg-indigo-50/50' : 'border-slate-100'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-xl bg-${deptInfo.color}-100 flex-shrink-0`}>
                        <span className="text-2xl">{deptInfo.icon}</span>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium bg-${deptInfo.color}-100 text-${deptInfo.color}-700`}>
                            From: {deptInfo.name}
                          </span>
                          {!message.is_read && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-indigo-500 text-white">
                              NEW
                            </span>
                          )}
                          {message.reply && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              ✓ Replied
                            </span>
                          )}
                        </div>
                        <h3 className={`font-bold text-slate-800 mb-1 ${!message.is_read ? 'text-indigo-800' : ''}`}>
                          {message.subject}
                        </h3>
                        <p className="text-sm text-slate-500 line-clamp-2">{message.message}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(message.created_at).toLocaleDateString()}
                          </span>
                          <span>By: {message.sender_name || 'Unknown'}</span>
                        </div>
                      </div>

                      <ChevronDown className="w-5 h-5 text-slate-400 transform -rotate-90" />
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ShepherdCornerSection;
